import React, { useState } from "react";
import PropTypes from "prop-types";

// @mui
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Container from "@mui/material/Container";

// components
import Chronometer from "src/consuman-components/generic/chronometer";
import Iconify from "src/components/iconify";
import { useSnackbar } from "src/components/snackbar";
import { useTranslation } from "react-i18next";

// api
import { useSettingsContext } from "src/components/settings";
import { getAssignedTask, update } from "src/api/work-order-task-user";
import { useRefs } from "src/consuman-components/generic/context/refsContext";
import { useAuthContext } from "src/auth/hooks";
import { dateTimeFormatGrid } from "src/core/lib/formats";
import CustomBreadcrumbs from "src/components/custom-breadcrumbs";
import Submit from "src/consuman-components/generic/submit";
// ----------------------------------------------------------------------

const assignedTaskDrawer = React.forwardRef(({ ...props }, ref) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuthContext();
  const { workOrderUserAssignedRef } = useRefs();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [assignedTask, setAssignedTask] = useState(null);
  const settings = useSettingsContext();

  const onClosed = () => {
    setDrawerOpen(false);
    setAssignedTask(null);
  };

  const open = async () => {
    setAssignedTask(null);
    setDrawerOpen(true);
    try {
      const data = await getAssignedTask();
      setAssignedTask(data);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("error"), { variant: "error" });
    }
  };

  const onStopTask = async () => {
    if (!assignedTask?.id) return;

    try {
      await update(assignedTask.id);
      enqueueSnackbar(t("saved"), { variant: "success" });
      workOrderUserAssignedRef.current?.refresh();
      setDrawerOpen(false);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("error"), { variant: "error" });
    }
  };

  React.useImperativeHandle(ref, () => ({
    open,
  }));

  return (
    <Drawer
      PaperProps={{
        sx: { width: { xs: "100%", md: "50%" } },
      }}
      anchor="right"
      open={drawerOpen}
      onClose={onClosed}
    >
      {assignedTask && (
        <Container
          sx={{ marginTop: "3%" }}
          maxWidth={settings.themeStretch ? false : "lg"}
        >
          <CustomBreadcrumbs
            heading={t("viewGeneric", { label: t("workOrderUserAssigned") })}
            action={
              <Submit
                onClick={onClosed}
                showSave={false}
                back
              />
            }
            sx={{
              mb: { xs: 3, md: 2 },
            }}
          />
          <Box sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Card>
                <CardContent>
                  <Grid
                    container
                    spacing={2}
                  >
                    <Grid
                      item
                      xs={12}
                      sm={6}
                    >
                      <Stack spacing={1}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                        >
                          {t("workOrder")}
                        </Typography>
                        <Typography variant="body2">#{assignedTask.woNumber}</Typography>
                      </Stack>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={6}
                    >
                      <Stack spacing={1}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                        >
                          {t("asset")}
                        </Typography>
                        <Typography variant="body2">{assignedTask.asset}</Typography>
                      </Stack>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={6}
                    >
                      <Stack spacing={1}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                        >
                          {t("label:component")}
                        </Typography>
                        <Typography variant="body2">{assignedTask.component}</Typography>
                      </Stack>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={6}
                    >
                      <Stack spacing={1}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                        >
                          {t("typeOf", { label: t("asset") })}
                        </Typography>
                        <Typography variant="body2">{assignedTask.assetType}</Typography>
                      </Stack>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={6}
                    >
                      <Stack spacing={1}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                        >
                          {t("keyLabel", { label: t("asset") })}
                        </Typography>
                        <Typography variant="body2">{assignedTask.assetKey}</Typography>
                      </Stack>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={6}
                    >
                      <Stack spacing={1}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                        >
                          {t("time")}
                        </Typography>
                        <Chronometer startDate={new Date(assignedTask.startDate)} />
                      </Stack>
                    </Grid>

                    {assignedTask.plannedDate && (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                      >
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                          >
                            {t("plannedDateTime")}
                          </Typography>
                          <Typography variant="body2">
                            {new Date(assignedTask.plannedDate).toLocaleString(
                              undefined,
                              dateTimeFormatGrid(user?.dateTimeFormat)
                            )}
                          </Typography>
                        </Stack>
                      </Grid>
                    )}

                    {assignedTask.assetTags && assignedTask.assetTags.length > 0 && (
                      <Grid
                        item
                        xs={12}
                      >
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                          >
                            {t("tags")}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            {assignedTask.assetTags.map((tag, index) => (
                              <Typography
                                key={index}
                                variant="caption"
                                sx={{
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 0.5,
                                  bgcolor: "divider",
                                }}
                              >
                                {tag}
                              </Typography>
                            ))}
                          </Box>
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              <Tooltip title={t("stop")}>
                <Button
                  fullWidth
                  variant="contained"
                  color="error"
                  startIcon={<Iconify icon="material-symbols:stop-circle-outline" />}
                  onClick={onStopTask}
                >
                  {t("stop")}
                </Button>
              </Tooltip>
            </Stack>
          </Box>
        </Container>
      )}
    </Drawer>
  );
});

assignedTaskDrawer.propTypes = {
  byProvider: PropTypes.any,
  provider: PropTypes.any,
  onChange: PropTypes.any,
  workOrderTaskId: PropTypes.any,
  disabled: PropTypes.any,
};

export default assignedTaskDrawer;
